import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faUser,
    faClock,
    faFileInvoiceDollar,
    faArrowRight,
    faChevronDown,
    faChevronUp,
    faSpinner,
    faChevronLeft,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { formatNumberWithDots, getDateAsReadableFormat } from '../../helper/general';
import ApiRoute from '../../helper/api';

function LogProductSidebar({ isOpen, onClose, selectedProduct }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [expandedLogs, setExpandedLogs] = useState({});
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
        from: 0,
        to: 0
    });

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            setTimeout(() => setIsAnimating(true), 10);
            // بارگذاری لاگ‌ها
            if (selectedProduct?._id) {
                loadLogs(selectedProduct._id);
            }
        } else {
            setIsAnimating(false);
            setTimeout(() => {
                setIsVisible(false);
                // reset state ها
                setLogs([]);
                setExpandedLogs({});
                setIsLoading(false);
                setPagination({
                    current_page: 1,
                    last_page: 1,
                    total: 0,
                    per_page: 10,
                    from: 0,
                    to: 0
                });
            }, 300);
        }
    }, [isOpen, selectedProduct]);

    const loadLogs = async (productId, page = 1) => {
        setIsLoading(true);
        try {
            // اضافه کردن پارامتر page به query string
            const queryParams = { page };
            const response = await ApiRoute.product.logs.fn(productId, queryParams);
            
            if (response?.status_code === 200 && response.results) {
                const { data, ...paginationData } = response.results;
                setLogs(data || []);
                setPagination({
                    current_page: paginationData.current_page || 1,
                    last_page: paginationData.last_page || 1,
                    total: paginationData.total || 0,
                    per_page: paginationData.per_page || 10,
                    from: paginationData.from || 0,
                    to: paginationData.to || 0
                });
            }
        } catch (error) {
            console.error('خطا در بارگذاری لاگ‌ها:', error);
            setLogs([]);
            setPagination({
                current_page: 1,
                last_page: 1,
                total: 0,
                per_page: 10,
                from: 0,
                to: 0
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page && selectedProduct?._id) {
            loadLogs(selectedProduct._id, page);
        }
    };

    const toggleLogExpansion = (logId) => {
        setExpandedLogs(prev => ({
            ...prev,
            [logId]: !prev[logId]
        }));
    };


    // تابع برای تشخیص نوع تغییر
    const getActionType = (action) => {
        if (action.includes('price')) return 'price_change';
        if (action.includes('stock')) return 'stock_update';
        if (action.includes('status')) return 'status_change';
        if (action.includes('private') || action.includes('publish')) return 'visibility_change';
        if (action.includes('base')) return 'base_info_change';
        return 'other';
    };

    // تابع برای ترجمه action
    const translateAction = (action) => {
        const translations = {
            'update_product_private': 'تغییر وضعیت انتشار',
            'update_product_price': 'بروزرسانی قیمت',
            'update_product_stock': 'بروزرسانی موجودی',
            'update_product_status': 'تغییر وضعیت',
            'update_product_info': 'ویرایش اطلاعات',
            'update_inventory': 'ویرایش انبار',
            'update_product_code': 'ویرایش شناسه محصول',
            'update_product_on_sale': 'بروزرسانی تاریخ فروش',
            'update_product_shipping': 'بروزرسانی کلاس ارسال',
            'update_product_base': 'ویرایش اطلاعات پایه محصول',
            'update_product_attributes': 'ویرایش ویژگی های محصول',
            'update_product_metadata': 'ویرایش اطلاعات اضافی محصول',
        };
        return translations[action] || action;
    };

    // تابع برای مقایسه و نمایش تغییرات
    const getChanges = (beforeData, afterData) => {
        const changes = [];
        
        // بررسی تغییرات قیمت
        if (beforeData.prices && afterData.prices) {
            Object.keys(afterData.prices).forEach(priceType => {
                if (beforeData.prices[priceType] !== afterData.prices[priceType]) {
                    const priceNames = {
                        price: 'قیمت اصلی',
                        regular: 'قیمت ویژه عادی',
                        sale: 'قیمت ویژه فوری',
                        reference: 'قیمت مرجع',
                        reward_credit: 'قیمت اعتبار'
                    };
                    changes.push({
                        field: priceNames[priceType] || priceType,
                        before: formatNumberWithDots(beforeData.prices[priceType]),
                        after: formatNumberWithDots(afterData.prices[priceType]),
                        type: 'price'
                    });
                }
            });
        }

        // بررسی تغییرات موجودی
        if (beforeData.stocks && afterData.stocks) {
            Object.keys(afterData.stocks).forEach(stockType => {
                if (beforeData.stocks[stockType] !== afterData.stocks[stockType]) {
                    const stockNames = {
                        web_site: 'موجودی سایت',
                        live: 'موجودی زنده',
                        iran: 'موجودی ایران',
                        kourosh: 'موجودی پردیس',
                        franchise: 'موجودی فصلی',
                        company: 'موجودی شرکت',
                        reservation: 'رزرو'
                    };
                    changes.push({
                        field: stockNames[stockType] || stockType,
                        before: beforeData.stocks[stockType],
                        after: afterData.stocks[stockType],
                        type: 'stock'
                    });
                }
            });
        }

        // بررسی تغییرات وضعیت
        if (beforeData.status !== afterData.status) {
            const statusNames = {
                'private': 'خصوصی',
                'publish': 'منتشر شده',
                'draft': 'پیشنویس'
            };
            changes.push({
                field: 'وضعیت انتشار',
                before: statusNames[beforeData.status] || beforeData.status,
                after: statusNames[afterData.status] || afterData.status,
                type: 'status'
            });
        }

        // بررسی تغییر شناسه محصول
        if (beforeData.product_id !== afterData.product_id) {
            changes.push({
                field: 'شناسه محصول',
                before: beforeData.product_id,
                after: afterData.product_id,
                type: 'string'
            });
        }

        if (beforeData.asan_id !== afterData.asan_id) {
            changes.push({
                field: 'شناسه اسان',
                before: beforeData.asan_id,
                after: afterData.asan_id,
                type: 'string'
            });
        }

        // بررسی تغییرات on_sale
        if (beforeData.on_sale || afterData.on_sale) {
            const beforeOnSale = beforeData.on_sale || {};
            const afterOnSale = afterData.on_sale || {};
            
            // بررسی تاریخ شروع
            if (beforeOnSale.start !== afterOnSale.start) {
                const formatDate = (date) => {
                    if (!date || date === null) return 'تعریف نشده';
                    return getDateAsReadableFormat(date, true);
                };
                
                changes.push({
                    field: 'تاریخ شروع فروش',
                    before: formatDate(beforeOnSale.start),
                    after: formatDate(afterOnSale.start),
                    type: 'date'
                });
            }
            
            // بررسی تاریخ پایان
            if (beforeOnSale.end !== afterOnSale.end) {
                const formatDate = (date) => {
                    if (!date || date === null) return 'تعریف نشده';
                    return getDateAsReadableFormat(date, true);
                };
                
                changes.push({
                    field: 'تاریخ پایان فروش',
                    before: formatDate(beforeOnSale.start),
                    after: formatDate(afterOnSale.end),
                    type: 'date'
                });
            }
        }

        // بررسی تغییرات categories
        if (beforeData.categories || afterData.categories) {
            const beforeCategories = beforeData.categories || [];
            const afterCategories = afterData.categories || [];
            
            // بررسی تغییرات در دسته‌بندی‌ها
            const hasChanged = beforeCategories.length !== afterCategories.length || 
                              beforeCategories.some(cat => !afterCategories.includes(cat)) ||
                              afterCategories.some(cat => !beforeCategories.includes(cat));
            
            if (hasChanged) {
                const formatCategories = (categories) => {
                    if (categories.length === 0) return 'هیچ دسته‌بندی';
                    
                    const translations = {
                        'product': 'محصول',
                        'refrigerator-and-freezer': 'یخچال و فریزر',
                        'side-by-side': 'ساید بای ساید',
                        'free-shipping': 'ارسال رایگان',
                        'free-shipping-tehran-and-karaj': 'ارسال رایگان تهران و البرز',
                        'small-pot': 'ظرف کوچک'
                    };
                    
                    return categories.map(cat => translations[cat] || cat).join(', ');
                };
                
                // شناسایی تغییرات دقیق‌تر
                const removedCategories = beforeCategories.filter(cat => !afterCategories.includes(cat));
                const addedCategories = afterCategories.filter(cat => !beforeCategories.includes(cat));
                
                if (removedCategories.length > 0) {
                    changes.push({
                        field: 'دسته‌بندی‌های حذف شده',
                        before: formatCategories(removedCategories),
                        after: 'حذف شده',
                        type: 'categories_removed'
                    });
                }
                
                if (addedCategories.length > 0) {
                    changes.push({
                        field: 'دسته‌بندی‌های اضافه شده',
                        before: 'اضافه نشده',
                        after: formatCategories(addedCategories),
                        type: 'categories_added'
                    });
                }
                
                // اگر فقط ترتیب تغییر کرده
                if (removedCategories.length === 0 && addedCategories.length === 0 && 
                    beforeCategories.length === afterCategories.length) {
                    changes.push({
                        field: 'ترتیب دسته‌بندی‌ها',
                        before: formatCategories(beforeCategories),
                        after: formatCategories(afterCategories),
                        type: 'categories_order'
                    });
                }
            }
        }

        // بررسی تغییرات tags
        if (beforeData.tags || afterData.tags) {
            const beforeTags = beforeData.tags || [];
            const afterTags = afterData.tags || [];
            
            const hasChanged = beforeTags.length !== afterTags.length || 
                              beforeTags.some(tag => !afterTags.includes(tag)) ||
                              afterTags.some(tag => !beforeTags.includes(tag));
            
            if (hasChanged) {
                const formatTags = (tags) => {
                    if (tags.length === 0) return 'هیچ تگی';
                    return tags.join(', ');
                };
                
                // شناسایی تغییرات دقیق‌تر
                const removedTags = beforeTags.filter(tag => !afterTags.includes(tag));
                const addedTags = afterTags.filter(tag => !beforeTags.includes(tag));
                
                if (removedTags.length > 0) {
                    changes.push({
                        field: 'تگ‌های حذف شده',
                        before: formatTags(removedTags),
                        after: 'حذف شده',
                        type: 'tags_removed'
                    });
                }
                
                if (addedTags.length > 0) {
                    changes.push({
                        field: 'تگ‌های اضافه شده',
                        before: 'اضافه نشده',
                        after: formatTags(addedTags),
                        type: 'tags_added'
                    });
                }
                
                // اگر فقط ترتیب تغییر کرده
                if (removedTags.length === 0 && addedTags.length === 0 && 
                    beforeTags.length === afterTags.length) {
                    changes.push({
                        field: 'ترتیب تگ‌ها',
                        before: formatTags(beforeTags),
                        after: formatTags(afterTags),
                        type: 'tags_order'
                    });
                }
            }
        }

        // بررسی تغییرات attributes
        if (beforeData.attributes || afterData.attributes) {
            const beforeAttrs = beforeData.attributes || {};
            const afterAttrs = afterData.attributes || {};
            
            // بررسی تغییرات در attributes
            const allAttrKeys = [...new Set([...Object.keys(beforeAttrs), ...Object.keys(afterAttrs)])];
            
            allAttrKeys.forEach(attrKey => {
                const beforeAttr = beforeAttrs[attrKey];
                const afterAttr = afterAttrs[attrKey];
                
                if (JSON.stringify(beforeAttr) !== JSON.stringify(afterAttr)) {
                    const formatAttributeValue = (attr) => {
                        if (!attr || !attr.values) return 'تعریف نشده';
                        return attr.values.map(val => val.label).join(', ');
                    };
                    
                    // استفاده از label موجود در دیتا به جای hardcode کردن
                    const getAttributeLabel = (attr) => {
                        if (attr && attr.label) {
                            return attr.label;
                        }
                        // fallback برای مواردی که label ندارند
                        const fallbackLabels = {
                            'brands': 'برند',
                            'color': 'رنگ',
                            'weight': 'وزن',
                            'energy': 'مصرف انرژی',
                            'type-refri': 'نوع یخچال',
                            'capacity-refrigerator': 'گنجایش یخچال',
                            'capacity-freezer': 'گنجایش فریزر',
                            'capacity-general': 'گنجایش کل',
                            'height-ref': 'ارتفاع',
                            'depth-refr': 'عمق',
                            'width-refr': 'پهنا',
                            'no-thriller': 'بدون برفک',
                            'number-of-floors': 'تعداد طبقات',
                            'number-of-door-classes': 'تعداد طبقات درب',
                            'number-of-drawer': 'تعداد کشو',
                            'to-open-the-door': 'جهت باز شدن درب',
                            'no-thriller-freezer': 'بدون برفک فریزر',
                            'number-of-floors-freezer': 'تعداد طبقات فریزر',
                            'number-door-freezer': 'تعداد طبقات درب فریزر',
                            'number-of-drawer-freezer': 'تعداد کشوی فریزر',
                            'to-open-the-freezer-door': 'جهت باز شدن درب فریزر',
                            'ice-maker': 'نوع یخساز',
                            'alarm': 'اخطار باز ماندن درب',
                            'water-cooler': 'آبسردکن',
                            'need-for-water-connection': 'لزوم اتصال به شیر آب',
                            'other-features': 'سایر ویژگی ها',
                            'sound-volume': 'میزان صدا',
                            'companion-items': 'اقلام همراه',
                            'automatic-door-closing-system': 'سامانه خودکار بسته شدن دربها',
                            'compressor-type': 'نوع کمپرسور',
                            'home-bar': 'بار خانگی',
                            'freezer-quick-freezing': 'انجماد سریع',
                            'refrigerator-fast-cooling': 'سرمایش سریع'
                        };
                        return fallbackLabels[attrKey] || attrKey;
                    };
                    
                    // استفاده از label از after_data (یا before_data اگر after_data موجود نباشد)
                    const attributeLabel = getAttributeLabel(afterAttr || beforeAttr);
                    
                    // بررسی تغییرات در group_label
                    if (beforeAttr?.group_label !== afterAttr?.group_label) {
                        changes.push({
                            field: `${attributeLabel} - گروه`,
                            before: beforeAttr?.group_label || 'تعریف نشده',
                            after: afterAttr?.group_label || 'تعریف نشده',
                            type: 'attribute_group'
                        });
                    }
                    
                    // بررسی تغییرات در values
                    const beforeValues = beforeAttr?.values || [];
                    const afterValues = afterAttr?.values || [];
                    
                    if (JSON.stringify(beforeValues) !== JSON.stringify(afterValues)) {
                        // بررسی تغییرات در تعداد values
                        if (beforeValues.length !== afterValues.length) {
                            changes.push({
                                field: `${attributeLabel} - تعداد مقادیر`,
                                before: beforeValues.length.toString(),
                                after: afterValues.length.toString(),
                                type: 'attribute_count'
                            });
                        }
                        
                        // بررسی تغییرات در محتوای values
                        const beforeValueLabels = beforeValues.map(v => v.label).join(', ');
                        const afterValueLabels = afterValues.map(v => v.label).join(', ');
                        
                        if (beforeValueLabels !== afterValueLabels) {
                            changes.push({
                                field: `${attributeLabel} - مقادیر`,
                                before: beforeValueLabels || 'تعریف نشده',
                                after: afterValueLabels || 'تعریف نشده',
                                type: 'attribute'
                            });
                        }
                    }
                }
            });
        }

        // بررسی تغییرات variations
        if (beforeData.variations || afterData.variations) {
            const beforeVariations = beforeData.variations || [];
            const afterVariations = afterData.variations || [];
            
            const hasChanged = beforeVariations.length !== afterVariations.length || 
                              JSON.stringify(beforeVariations) !== JSON.stringify(afterVariations);
            
            if (hasChanged) {
                const formatVariations = (variations) => {
                    if (variations.length === 0) return 'هیچ تنوعی';
                    return variations.map(v => v.title).join(', ');
                };
                
                changes.push({
                    field: 'تنوع‌های محصول',
                    before: formatVariations(beforeVariations),
                    after: formatVariations(afterVariations),
                    type: 'variations'
                });
            }
        }

        // بررسی تغییرات metadata
        if (beforeData.metadata || afterData.metadata) {
            const beforeMeta = beforeData.metadata || {};
            const afterMeta = afterData.metadata || {};
            
            ['stock_name', 'guarantee', 'custom_attributes', 'subtitle'].forEach(field => {
                if (beforeMeta[field] !== afterMeta[field]) {
                    const fieldNames = {
                        'stock_name': 'نام موجودی',
                        'guarantee': 'گارانتی',
                        'custom_attributes': 'ویژگی های سفارشی',
                        'subtitle': 'زیرعنوان'
                    };
                    
                    changes.push({
                        field: fieldNames[field] || field,
                        before: beforeMeta[field] || 'تعریف نشده',
                        after: afterMeta[field] || 'تعریف نشده',
                        type: 'metadata'
                    });
                }
            });
        }

        // بررسی تغییرات cards
        if (beforeData.cards || afterData.cards) {
            const beforeCards = beforeData.cards || [];
            const afterCards = afterData.cards || [];
            
            const hasChanged = beforeCards.length !== afterCards.length || 
                              JSON.stringify(beforeCards) !== JSON.stringify(afterCards);
            
            if (hasChanged) {
                const formatCards = (cards) => {
                    if (cards.length === 0) return 'هیچ کارتی';
                    return cards.map(c => c.title).join(', ');
                };
                
                changes.push({
                    field: 'کارت‌های محصول',
                    before: formatCards(beforeCards),
                    after: formatCards(afterCards),
                    type: 'cards'
                });
            }
        }

        // بررسی سایر تغییرات
        ['is_reviewable', 'has_today_shipment', 'has_presell'].forEach(field => {
            if (beforeData[field] !== afterData[field]) {
                const fieldNames = {
                    'is_reviewable': 'تایید کالا',
                    'has_today_shipment': 'ارسال امروز',
                    'has_presell': 'پیش فروش'
                };
                changes.push({
                    field: fieldNames[field] || field,
                    before: beforeData[field] ? 'بله' : 'خیر',
                    after: afterData[field] ? 'بله' : 'خیر',
                    type: 'boolean'
                });
            }
        });

        return changes;
    };

    if (!isVisible) return null;

    return (
        <>
            {/* پس‌زمینه تاریک */}
            <div 
                className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out ${
                    isAnimating ? 'opacity-50' : 'opacity-0'
                }`}
                onClick={onClose}
            ></div>
            
            {/* سایدبار */}
            <div className={`fixed left-0 top-0 h-full w-[45rem] bg-white shadow-xl z-50 transform transition-all duration-300 ease-in-out ${
                isAnimating ? 'translate-x-0' : '-translate-x-full'
            }`}>
                {/* هدر سایدبار */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                            لاگ‌های محصول
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {selectedProduct?.title}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
                    </button>
                </div>

                {/* محتوای لاگ‌ها */}
                <div className="h-full overflow-y-auto pb-20">
                    <div className="p-4 space-y-4">
                        {isLoading ? (
                            <div className="text-center py-8">
                                <FontAwesomeIcon 
                                    icon={faSpinner} 
                                    className="text-gray-400 text-2xl mb-3 animate-spin"
                                />
                                <p className="text-gray-500">در حال بارگذاری لاگ‌ها...</p>
                            </div>
                        ) : logs.length > 0 ? (
                            logs.map((log, index) => {
                                const changes = getChanges(log.before_data, log.after_data);
                                const actionType = getActionType(log.action);
                                const isExpanded = expandedLogs[log._id];

                                return (
                                    <div
                                        key={log._id}
                                        className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 transform ${
                                            isAnimating 
                                                ? 'translate-x-0 opacity-100' 
                                                : 'translate-x-4 opacity-0'
                                        }`}
                                        style={{
                                            transitionDelay: isAnimating ? `${index * 50}ms` : '0ms'
                                        }}
                                    >
                                        {/* هدر لاگ */}
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-3 h-3 rounded-full ${
                                                    actionType === 'price_change' ? 'bg-blue-500' :
                                                    actionType === 'stock_update' ? 'bg-green-500' :
                                                    actionType === 'status_change' ? 'bg-yellow-500' :
                                                    actionType === 'visibility_change' ? 'bg-purple-500' :
                                                    'bg-orange-500'
                                                }`}></div>
                                                <span className="font-semibold text-gray-800 text-sm">
                                                    {translateAction(log.action)}
                                                </span>
                                            </div>
                                            
                                            {changes.length > 0 && (
                                                <button
                                                    onClick={() => toggleLogExpansion(log._id)}
                                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                                >
                                                    <FontAwesomeIcon 
                                                        icon={isExpanded ? faChevronUp : faChevronDown} 
                                                        className="text-xs"
                                                    />
                                                </button>
                                            )}
                                        </div>

                                        {/* خلاصه تغییرات */}
                                        <div className="text-sm text-gray-600 mb-3">
                                            {changes.length > 0 ? (
                                                <span>{changes.length} مورد تغییر انجام شد</span>
                                            ) : (
                                                <span>هیچ تغییری شناسایی نشد</span>
                                            )}
                                        </div>

                                        {/* جزئیات تغییرات */}
                                        {isExpanded && changes.length > 0 && (
                                            <div className="border-t border-gray-100 pt-3 mt-3 space-y-2">
                                                {changes.map((change, changeIndex) => (
                                                    <div key={changeIndex} className="flex items-center justify-between text-xs bg-gray-50 rounded p-2">
                                                        <span className="font-medium text-gray-700">
                                                            {change.field}:
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-1 rounded ${
                                                                change.type === 'price' ? 'bg-green-100 text-green-700' :
                                                                change.type === 'stock' ? 'bg-blue-100 text-blue-700' :
                                                                change.type === 'string' ? 'bg-orange-100 text-orange-700' :
                                                                change.type === 'date' ? 'bg-purple-100 text-purple-700' :
                                                                change.type === 'shipping' ? 'bg-teal-100 text-teal-700' :
                                                                change.type === 'categories' ? 'bg-indigo-100 text-indigo-700' :
                                                                change.type === 'categories_removed' ? 'bg-red-100 text-red-700' :
                                                                change.type === 'categories_added' ? 'bg-green-100 text-green-700' :
                                                                change.type === 'categories_order' ? 'bg-yellow-100 text-yellow-700' :
                                                                change.type === 'tags' ? 'bg-pink-100 text-pink-700' :
                                                                change.type === 'tags_removed' ? 'bg-red-100 text-red-700' :
                                                                change.type === 'tags_added' ? 'bg-green-100 text-green-700' :
                                                                change.type === 'tags_order' ? 'bg-yellow-100 text-yellow-700' :
                                                                change.type === 'attribute' ? 'bg-amber-100 text-amber-700' :
                                                                change.type === 'attribute_group' ? 'bg-violet-100 text-violet-700' :
                                                                change.type === 'attribute_count' ? 'bg-slate-100 text-slate-700' :
                                                                change.type === 'variations' ? 'bg-cyan-100 text-cyan-700' :
                                                                change.type === 'metadata' ? 'bg-emerald-100 text-emerald-700' :
                                                                change.type === 'cards' ? 'bg-rose-100 text-rose-700' :
                                                                'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {change.after}
                                                            </span>
                                                            <FontAwesomeIcon icon={faArrowRight} className="text-gray-400" />
                                                            <span className={`px-2 py-1 rounded ${
                                                                change.type === 'price' ? 'bg-red-100 text-red-700' :
                                                                change.type === 'stock' ? 'bg-blue-100 text-blue-700' :
                                                                change.type === 'string' ? 'bg-orange-100 text-orange-700' :
                                                                change.type === 'date' ? 'bg-purple-100 text-purple-700' :
                                                                change.type === 'shipping' ? 'bg-teal-100 text-teal-700' :
                                                                change.type === 'categories' ? 'bg-indigo-100 text-indigo-700' :
                                                                change.type === 'categories_removed' ? 'bg-red-100 text-red-700' :
                                                                change.type === 'categories_added' ? 'bg-green-100 text-green-700' :
                                                                change.type === 'categories_order' ? 'bg-yellow-100 text-yellow-700' :
                                                                change.type === 'tags' ? 'bg-pink-100 text-pink-700' :
                                                                change.type === 'tags_removed' ? 'bg-red-100 text-red-700' :
                                                                change.type === 'tags_added' ? 'bg-green-100 text-green-700' :
                                                                change.type === 'tags_order' ? 'bg-yellow-100 text-yellow-700' :
                                                                change.type === 'attribute' ? 'bg-amber-100 text-amber-700' :
                                                                change.type === 'attribute_group' ? 'bg-violet-100 text-violet-700' :
                                                                change.type === 'attribute_count' ? 'bg-slate-100 text-slate-700' :
                                                                change.type === 'variations' ? 'bg-cyan-100 text-cyan-700' :
                                                                change.type === 'metadata' ? 'bg-emerald-100 text-emerald-700' :
                                                                change.type === 'cards' ? 'bg-rose-100 text-rose-700' :
                                                                'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {change.before}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* اطلاعات کاربر و زمان */}
                                        <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2 mt-3">
                                            <div className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faUser} />
                                                <span>{log.employee?.name || 'نامشخص'}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faClock} />
                                                <span>{getDateAsReadableFormat(
                                                log.created_at,
                                                true
                                            )}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={`text-center py-8 transition-all duration-500 transform ${
                                isAnimating 
                                    ? 'translate-y-0 opacity-100' 
                                    : 'translate-y-4 opacity-0'
                            }`}>
                                <FontAwesomeIcon 
                                    icon={faFileInvoiceDollar} 
                                    className="text-gray-400 text-3xl mb-3"
                                />
                                <p className="text-gray-500">
                                    هیچ لاگی برای این محصول ثبت نشده است
                                </p>
                            </div>
                        )}
                        
                        {/* کامپوننت صفحه‌بندی */}
                        {!isLoading && logs.length > 0 && pagination.last_page > 1 && (
                            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 mt-4">
                                <div className="flex items-center justify-between text-sm">
                                    {/* اطلاعات صفحه‌بندی */}
                                    <div className="text-gray-600">
                                        نمایش {pagination.from} تا {pagination.to} از {pagination.total} مورد
                                    </div>
                                    
                                    {/* دکمه‌های ناوبری */}
                                    <div className="flex items-center gap-2">
                                        {/* دکمه صفحه قبل */}
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={pagination.current_page <= 1}
                                            className={`p-2 rounded-lg transition-colors ${
                                                pagination.current_page <= 1
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                                        </button>
                                        
                                        {/* شماره صفحات */}
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                                let pageNum;
                                                if (pagination.last_page <= 5) {
                                                    pageNum = i + 1;
                                                } else {
                                                    if (pagination.current_page <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.current_page >= pagination.last_page - 2) {
                                                        pageNum = pagination.last_page - 4 + i;
                                                    } else {
                                                        pageNum = pagination.current_page - 2 + i;
                                                    }
                                                }
                                                
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handlePageChange(pageNum)}
                                                        className={`w-8 h-8 rounded-lg text-xs transition-colors ${
                                                            pageNum === pagination.current_page
                                                                ? 'bg-blue-500 text-white'
                                                                : 'text-gray-600 hover:bg-gray-100'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        
                                        {/* دکمه صفحه بعد */}
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={pagination.current_page >= pagination.last_page}
                                            className={`p-2 rounded-lg transition-colors ${
                                                pagination.current_page >= pagination.last_page
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default LogProductSidebar; 